import Button from '@atlaskit/button/default/button';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
	BaseStepButtonProps,
	StepButtonProps,
	StepCallback,
	StepContextProps,
	StepContextProviderProps,
} from './types';
import { type JSX } from 'react/jsx-runtime';

const initialState: StepContextProps = {
	NextButton: () => {
		// eslint-disable-next-line no-console
		console.error('NextButton has not been implemented. You may have forgotten to add a provider');

		return null;
	},
	PreviousButton: () => {
		// eslint-disable-next-line no-console
		console.error('Previous has not been implemented. You may have forgotten to add a provider');

		return null;
	},
	next: () => {
		// eslint-disable-next-line no-console
		console.error('next has not been implemented. You may have forgotten to add a provider');
	},
	previous: () => {
		// eslint-disable-next-line no-console
		console.error('previous has not been implemented. You may have forgotten to add a provider');
	},
	currentStep: -1,
	totalSteps: 0,
	offsetCurrentStepIndex: -1,
	offsetTotalStepsIndex: 0,
};

const StepContext = createContext<StepContextProps>(initialState);

/**
 * useStepContext is used to manage steps in a multi-step process.
 *
 * This hook returns an object with properties and components useful for navigating
 * through steps, including button components for moving to the next and previous steps.
 *
 * @property {React.ComponentType<StepButtonProps>} NextButton - A component for the next step button. It provides useful defaults and accepts properties for customization.
 * @property {React.ComponentType<StepButtonProps>} PreviousButton - A component for the previous step button.  It provides useful defaults and accepts properties for customization.
 * @property {StepCallback} next - A callback function to proceed to the next step. If you're not using the provided component you can use this to use your own button component.
 * @property {StepCallback} previous - A callback function to go back to the previous step. If you're not using the provided component you can use this to use your own button component
 * @property {number} currentStep - The index of the current step, starting from 1.
 * @property {number} totalSteps - The total number of steps.
 * @property {number | undefined} offsetCurrentStepIndex - The index of the current step, adjusted by an offset if applicable. It can be undefined.
 * @property {number} offsetTotalStepsIndex - The total number of steps, adjusted by an offset if applicable.
 *
 * @example const {
 * 		NextButton,
 * 		PreviousButton,
 * 		next,
 * 		previous,
 * 		currentStep,
 * 		totalSteps,
 * 		offsetCurrentStepIndex,
 * 		offsetTotalStepsIndex
 * } = useStepContext();
 **/
export const useStepContext = (): StepContextProps => useContext(StepContext);

export const StepContextProvider = ({
	steps,
	initialStep = 1,
	children,
	progressIndexOffset = 0,
	onEnd,
}: StepContextProviderProps): JSX.Element => {
	// Note: Step counter is 1 indexed
	const [{ currentStep }, setStepState] = React.useState<{
		currentStep: number;
		previousStep?: number;
	}>({
		currentStep: initialStep,
	});

	//const { messageStep } = useMessageEvent();

	const totalSteps = steps.length;

	const offsetCurrentStepIndex = currentStep - progressIndexOffset || undefined;
	const offsetTotalStepsIndex = totalSteps - progressIndexOffset;

	// Allows users to pass in a custom component to handle next or previous step
	// <Next customComponent={MyComponent} />
	const withCustomComponent = useCallback(
		(DefaultComponent: React.FC<BaseStepButtonProps>) =>
			({ customComponent: CustomComponent, text }: StepButtonProps, clickHandler: () => void) => {
				const isCtaStepButtonTextEnabled = FeatureGates.checkGate(
					'post-office_cta-step-button-text',
				);

				if (CustomComponent) {
					return (
						<CustomComponent
							text={text}
							currentStep={currentStep}
							totalSteps={totalSteps}
							StepStateText={
								<NextButtonStateText
									currentStep={currentStep}
									totalSteps={totalSteps}
									progressIndexOffset={progressIndexOffset}
								/>
							}
							clickHandler={clickHandler}
						/>
					);
				}

				return isCtaStepButtonTextEnabled ? <DefaultComponent text={text} /> : <DefaultComponent />;
			},
		[currentStep, totalSteps, progressIndexOffset],
	);

	/*-- Next -- */

	const handleEnd = useCallback(() => {
		onEnd?.();
	}, [onEnd]);

	/*-- Next -- */

	const handleNext = useCallback<StepCallback>(() => {
		setStepState((prev) => {
			const nextStep = prev.currentStep + 1;

			if (nextStep > totalSteps) {
				handleEnd();
			}

			const next = {
				currentStep: Math.min(nextStep, totalSteps),
				previousStep: prev.currentStep,
			};

			// dispatch future message event here

			return next;
		});
	}, [totalSteps, handleEnd]);

	const NextButton = useCallback<React.FC<StepButtonProps>>(
		(props) =>
			withCustomComponent(({ text = <DefaultNextButtonText /> }) =>
				currentStep !== totalSteps ? (
					<Button onClick={() => handleNext(props)}>
						{text}
						{props.showProgress && (
							<NextButtonStateText
								currentStep={currentStep}
								totalSteps={totalSteps}
								progressIndexOffset={progressIndexOffset}
							/>
						)}
					</Button>
				) : null,
			)(props, () => handleNext(props)),
		[currentStep, totalSteps, handleNext, withCustomComponent, progressIndexOffset],
	);

	/*-- Previous -- */

	const handlePrevious = useCallback<StepCallback>(() => {
		setStepState((prev) => {
			const previous = {
				currentStep: Math.min(prev.currentStep - 1, totalSteps),
				previousStep: prev.currentStep,
			};

			// dispatch future message event here

			return previous;
		});
	}, [totalSteps]);

	const PreviousButton = useCallback<React.FC<StepButtonProps>>(
		(props) =>
			withCustomComponent(({ text = <DefaultPreviousButtonText /> }) =>
				currentStep !== 1 ? <Button onClick={() => handlePrevious(props)}>{text}</Button> : null,
			)(props, () => handlePrevious(props)),
		[handlePrevious, currentStep, withCustomComponent],
	);

	const value = useMemo<StepContextProps>(
		() => ({
			NextButton,
			PreviousButton,
			next: handleNext,
			previous: handlePrevious,
			currentStep,
			totalSteps,
			offsetCurrentStepIndex,
			offsetTotalStepsIndex,
		}),
		[
			NextButton,
			PreviousButton,
			currentStep,
			totalSteps,
			offsetCurrentStepIndex,
			offsetTotalStepsIndex,
			handleNext,
			handlePrevious,
		],
	);

	return <StepContext.Provider value={value}>{children}</StepContext.Provider>;
};

export const DefaultNextButtonText = (): JSX.Element => (
	<FormattedMessage
		id="post-office.components.in-app.step-context.default-next-button.main"
		defaultMessage="Next"
		description="This is the default text displayed on the next button when navigating between message steps"
	/>
);

const NextButtonStateText = ({
	currentStep,
	totalSteps,
	progressIndexOffset = 0,
}: {
	currentStep: number;
	totalSteps: number;
	progressIndexOffset: number;
}) => {
	const offsetCurrentStep = currentStep - progressIndexOffset;
	const offsetTotalStep = totalSteps - progressIndexOffset;

	return (
		<>
			{!!offsetCurrentStep && (
				<FormattedMessage
					id="post-office.components.in-app.step-context.next-button.step-state"
					defaultMessage="{currentStep} of {totalSteps}"
					description="This is the default text displayed on the next button when navigating between message steps"
					values={{
						currentStep: offsetCurrentStep,
						totalSteps: offsetTotalStep,
					}}
				/>
			)}
		</>
	);
};

export const DefaultPreviousButtonText = (): JSX.Element => (
	<FormattedMessage
		id="post-office.components.in-app.step-context.default-previous-button.main"
		defaultMessage="Back"
		description="This is the default text displayed on the previous button when navigating between message steps"
	/>
);
